"use client";

import { BrowserNotifier } from "@/components/BrowserNotifier";
import { isPublicPath } from "@/lib/public-paths";
import { useIsSiteHost } from "@/components/SiteHostContext";
import React, { useState, useEffect } from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useApp } from "./AppContext";
import { useI18n } from "@/i18n/I18nProvider";
import { LanguageSwitcher } from "@/i18n/LanguageSwitcher";
import { NewBookingModal } from "./NewBookingModal";
import { BrandLogo } from "./BrandMark";
import {
  OverviewIcon,
  CalendarIcon,
  BookingIcon,
  UnitIcon,
  PropertyIcon,
  GuestIcon,
  BellIcon,
  PlusIcon,
  CatalogIcon,
  OrderIcon,
  CreditCardIcon,
  ServiceRequestIcon,
  ActivityIcon,
  GiftIcon,
  TicketIcon,
  ChatIcon,
  StarIcon,
  GlobeIcon,
  CheckIcon,
  HomeIcon,
  MenuIcon,
  PrinterIcon,
  XIcon,
} from "./Icons";
import type { Notification } from "@/lib/types";
import { PLATFORM_TENANT_ID } from "@/lib/billing/constants";

export function AppShell({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const router = useRouter();
  const isSiteHost = useIsSiteHost();
  const {
    user,
    tenantId,
    role,
    availableTenants,
    properties,
    selectedPropertyId,
    setSelectedPropertyId,
    unreadCount,
    billing,
    impersonation,
    exitImpersonation,
    refreshNotifications,
    switchTenant,
    logout,
    fetchApi,
  } = useApp();
  const { t, tx, formatDateTime } = useI18n();

  const [isNewBookingOpen, setIsNewBookingOpen] = useState(false);
  const [isNotifOpen, setIsNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [chatUnread, setChatUnread] = useState(0);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // The phone menu sheet closes itself once a link in it has navigated.
  useEffect(() => {
    setIsMenuOpen(false);
    setIsNotifOpen(false);
  }, [pathname]);

  // Any page (e.g. the dashboard's quick actions) can open the global booking form.
  useEffect(() => {
    const open = () => setIsNewBookingOpen(true);
    window.addEventListener("villa_os_open_new_booking", open);
    return () => window.removeEventListener("villa_os_open_new_booking", open);
  }, []);

  // Subscription gate: a lapsed organization can only reach the paywall, account and notifications.
  const isPlatformOrg = tenantId === PLATFORM_TENANT_ID;
  const billingLocked = Boolean(billing?.locked) && !isPlatformOrg;
  const billingAllowedPath = pathname === "/subscription" || pathname === "/account" || pathname === "/notifications";
  useEffect(() => {
    if (billingLocked && role === "OWNER" && !billingAllowedPath) router.replace("/subscription");
    if (isPlatformOrg && pathname === "/") router.replace("/admin");
  }, [billingLocked, role, billingAllowedPath, isPlatformOrg, pathname, router]);

  // Unread chat count for the sidebar badge: refreshed on navigation and whenever the inbox reports a change.
  const canChat = role === "OWNER" || role === "MANAGER" || role === "FRONTDESK";
  useEffect(() => {
    if (!canChat || isPlatformOrg || isSiteHost || isPublicPath(pathname)) return;
    let cancelled = false;
    const load = () =>
      fetchApi("/api/chat/threads")
        .then((r) => r.json())
        .then((json) => {
          if (!cancelled && json.success) setChatUnread(json.data.unread_total);
        })
        .catch(() => {});
    load();
    const onChange = (e: Event) => setChatUnread((e as CustomEvent<number>).detail);
    window.addEventListener("chat:unread", onChange);
    return () => {
      cancelled = true;
      window.removeEventListener("chat:unread", onChange);
    };
  }, [canChat, isPlatformOrg, isSiteHost, pathname, fetchApi]);

  // Load preview notifications when popover opens
  useEffect(() => {
    if (isNotifOpen) {
      fetchApi("/api/notifications")
        .then((r) => r.json())
        .then((data) => {
          if (data.success) setNotifications(data.data);
        })
        .catch(console.error);
    }
  }, [isNotifOpen, fetchApi]);

  const handleMarkAsRead = async (notif: Notification) => {
    try {
      await fetchApi("/api/notifications", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: notif.id }),
      });
      refreshNotifications();
      setIsNotifOpen(false);
      if (notif.entity_id) {
        if (notif.entity_type === "ORDER") {
          router.push(`/orders/${notif.entity_id}`);
        } else if (notif.entity_type === "BOOKING") {
          router.push(`/bookings/${notif.entity_id}`);
        } else if (notif.entity_type === "SERVICE_REQUEST") {
          router.push(`/service-requests/${notif.entity_id}`);
        } else if (notif.entity_type === "ACTIVITY_SESSION") {
          router.push(`/activities/${notif.entity_id}`);
        } else if (notif.entity_type === "ACTIVITY_BOOKING") {
          router.push("/activities");
        } else if (notif.entity_type === "ENTITLEMENT") {
          router.push(`/entitlements/${notif.entity_id}`);
        } else if (notif.entity_type === "FEEDBACK") {
          router.push(`/feedback/${notif.entity_id}`);
        } else if (notif.entity_type === "SUBSCRIPTION") {
          router.push("/subscription");
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await fetchApi("/api/notifications", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ all: true }),
      });
      refreshNotifications();
      setIsNotifOpen(false);
    } catch (err) {
      console.error(err);
    }
  };

  // Public / Anonymous guest routes and login bypass the staff shell
  if (isSiteHost || isPublicPath(pathname)) {
    return <>{children}</>;
  }

  const currentProp = properties.find((p) => p.id === selectedPropertyId);
  const isOwnerOrManager = role === "OWNER" || role === "MANAGER";
  const canCreateBookings = !isPlatformOrg && !billingLocked && (role === "OWNER" || role === "MANAGER" || role === "FRONTDESK");

  // One list drives both the desktop sidebar and the phone menu sheet.
  type NavItem = { href: string; label: string; Icon: React.ComponentType<{ size?: number }>; active: boolean; badge?: string };
  const isAt = (href: string) => pathname === href;
  const isUnder = (href: string) => pathname.startsWith(href);
  const navGroups: { label: string; items: NavItem[] }[] = [];
  if (isPlatformOrg) {
    navGroups.push({
      label: t("nav.sections.platform"),
      items: [
        { href: "/admin", label: t("nav.items.customers"), Icon: PropertyIcon, active: pathname === "/admin" || isUnder("/admin/tenants") },
        { href: "/admin/onboarding", label: t("nav.items.adminOnboarding"), Icon: PlusIcon, active: isUnder("/admin/onboarding") },
        { href: "/admin/health", label: t("nav.items.adminHealth"), Icon: OverviewIcon, active: isAt("/admin/health") },
        { href: "/admin/users", label: t("nav.items.adminUsers"), Icon: GuestIcon, active: isAt("/admin/users") },
        { href: "/admin/properties", label: t("nav.items.adminProperties"), Icon: PropertyIcon, active: isAt("/admin/properties") },
        { href: "/admin/audit", label: t("nav.items.adminAudit"), Icon: ActivityIcon, active: isAt("/admin/audit") },
        { href: "/admin/plans", label: t("nav.items.plans"), Icon: CreditCardIcon, active: isAt("/admin/plans") },
        { href: "/admin/payouts", label: t("nav.items.payouts"), Icon: CreditCardIcon, active: isAt("/admin/payouts") },
      ],
    });
  } else {
    const ops: NavItem[] = [];
    if (role !== "SERVICE_STAFF") {
      ops.push(
        { href: "/", label: t("nav.items.overview"), Icon: OverviewIcon, active: isAt("/") },
        { href: "/calendar", label: t("nav.items.calendar"), Icon: CalendarIcon, active: isAt("/calendar") },
        { href: "/bookings", label: t("nav.items.reservations"), Icon: BookingIcon, active: isUnder("/bookings") },
        { href: "/chat", label: t("nav.items.chat"), Icon: ChatIcon, active: isUnder("/chat"), badge: chatUnread > 0 ? (chatUnread > 99 ? "99+" : String(chatUnread)) : undefined },
      );
    }
    ops.push(
      { href: "/orders", label: t("nav.items.orders"), Icon: OrderIcon, active: isUnder("/orders") },
      { href: "/service-requests", label: t("nav.items.requests"), Icon: ServiceRequestIcon, active: isUnder("/service-requests") },
      { href: "/activities", label: t("nav.items.activities"), Icon: ActivityIcon, active: isUnder("/activities") },
      { href: "/entitlements", label: t("nav.items.entitlements"), Icon: GiftIcon, active: isUnder("/entitlements") },
      { href: "/feedback", label: t("nav.items.feedback"), Icon: StarIcon, active: pathname === "/feedback" },
    );
    if (isOwnerOrManager) ops.push({ href: "/balance", label: t("nav.items.balance"), Icon: CreditCardIcon, active: isAt("/balance") });
    if (role !== "SERVICE_STAFF") ops.push({ href: "/payments", label: t("nav.items.payments"), Icon: CreditCardIcon, active: isUnder("/payments") });
    navGroups.push({ label: t("nav.sections.operations"), items: ops });

    if (isOwnerOrManager) {
      navGroups.push({
        label: t("nav.sections.portfolio"),
        items: [
          { href: "/properties", label: t("nav.items.properties"), Icon: PropertyIcon, active: isAt("/properties") },
          { href: "/units", label: t("nav.items.units"), Icon: UnitIcon, active: isAt("/units") },
          { href: "/guests", label: t("nav.items.guests"), Icon: GuestIcon, active: isAt("/guests") },
          { href: "/catalog", label: t("nav.items.catalog"), Icon: CatalogIcon, active: isAt("/catalog") },
          { href: "/booking-sites", label: t("nav.items.bookingSites"), Icon: GlobeIcon, active: isUnder("/booking-sites") },
          { href: "/packages", label: t("nav.items.packages"), Icon: GiftIcon, active: isAt("/packages") },
          { href: "/pricing", label: t("nav.items.pricing"), Icon: TicketIcon, active: isAt("/pricing") },
          { href: "/vouchers", label: t("nav.items.vouchers"), Icon: TicketIcon, active: isAt("/vouchers") },
        ],
      });
    }
  }
  const systemItems: NavItem[] = [];
  if (role === "OWNER" && !isPlatformOrg) {
    systemItems.push(
      { href: "/subscription", label: t("nav.items.subscription"), Icon: CreditCardIcon, active: isAt("/subscription"), badge: billingLocked ? "!" : undefined },
      { href: "/users", label: t("nav.items.team"), Icon: GuestIcon, active: isAt("/users") },
    );
  }
  systemItems.push(
    { href: "/account", label: t("nav.items.account"), Icon: CheckIcon, active: isAt("/account") },
    { href: "/notifications", label: t("nav.items.notifications"), Icon: BellIcon, active: isAt("/notifications"), badge: unreadCount > 0 ? String(unreadCount) : undefined },
  );
  navGroups.push({ label: t("nav.sections.system"), items: systemItems });

  // Phone bottom bar / tablet rail: the few destinations used every day; everything else sits behind "Menu".
  const tabItems: NavItem[] = isPlatformOrg
    ? navGroups[0].items.slice(0, 3)
    : role === "SERVICE_STAFF"
      ? navGroups[0].items.slice(0, 3)
      : [
          { href: "/", label: t("nav.mobile.home"), Icon: HomeIcon, active: isAt("/") },
          { href: "/calendar", label: t("nav.items.calendar"), Icon: CalendarIcon, active: isAt("/calendar") },
          { href: "/payments", label: t("nav.mobile.receipts"), Icon: PrinterIcon, active: isUnder("/payments") },
        ];
  const menuActive = !tabItems.some((i) => i.active);

  return (
    <div className="app-container">
      {/* AdminCN Sidebar */}
      <aside className="sidebar">
        <div className="sidebar-header">
          <BrandLogo size={24} />
          <span className="brand-badge-env">{role ? tx(`common.roles.${role}`) : t("nav.staff")}</span>
        </div>

        <nav className="sidebar-nav">
          {navGroups.map((group, gi) => (
            <React.Fragment key={group.label}>
              <div className="nav-section-label" style={gi > 0 ? { marginTop: "10px" } : undefined}>{group.label}</div>
              {group.items.map((item) => (
                <Link key={item.href} href={item.href} className={`nav-link ${item.active ? "active" : ""}`}>
                  <item.Icon size={14} />
                  <span>{item.label}</span>
                  {item.badge && <span className="nav-badge">{item.badge}</span>}
                </Link>
              ))}
            </React.Fragment>
          ))}
        </nav>

        <div className="sidebar-footer">
          {/* Organization Switcher */}
          <div className="tenant-selector-box" style={{ marginBottom: "12px" }}>
            <div className="tenant-label">{t("nav.org.label")}</div>
            {availableTenants.length > 1 ? (
              <select
                className="tenant-select"
                value={tenantId}
                onChange={(e) => switchTenant(e.target.value)}
                aria-label={t("nav.org.ariaLabel")}
              >
                {availableTenants.map((tn) => (
                  <option key={tn.tenant_id} value={tn.tenant_id}>
                    {tn.tenant_name} ({tx(`common.roles.${tn.role}`)})
                  </option>
                ))}
              </select>
            ) : (
              <div style={{ fontSize: "12px", fontWeight: 600, color: "var(--text-primary)" }}>
                {availableTenants[0]?.tenant_name || t("nav.org.fallback")}
              </div>
            )}
          </div>

          {/* User Profile & Logout */}
          {user && (
            <div
              style={{
                paddingTop: "10px",
                borderTop: "1px solid var(--border-subtle)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "8px", overflow: "hidden" }}>
                <div
                  style={{
                    width: "24px",
                    height: "24px",
                    borderRadius: "50%",
                    background: "#27272a",
                    color: "#ffffff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "10px",
                    fontWeight: 700,
                    flexShrink: 0,
                  }}
                >
                  {user.name.charAt(0).toUpperCase()}
                </div>
                <div style={{ overflow: "hidden" }}>
                  <div style={{ fontSize: "12px", fontWeight: 600, color: "var(--text-primary)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {user.name}
                  </div>
                  <div style={{ fontSize: "10px", color: "var(--text-muted)", textTransform: "none" }}>
                    {role ? tx(`common.roles.${role}`) : t("nav.staff")}
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={logout}
                className="btn btn-ghost btn-sm"
                style={{ height: "24px", padding: "0 6px", fontSize: "11px", color: "var(--text-muted)" }}
                title={t("nav.signOut")}
              >
                {t("nav.logout")}
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="main-wrapper">
        <header className="top-bar">
          <div className="top-bar-left">
            <span className="mobile-only top-bar-brand">
              <BrandLogo size={22} />
            </span>
            <span className="topbar-context-label desktop-only">
              {currentProp ? currentProp.name : t("nav.topbar.allProperties")}
            </span>

            {properties.length > 0 && (
              <select
                className="property-selector"
                value={selectedPropertyId}
                onChange={(e) => setSelectedPropertyId(e.target.value)}
                aria-label={t("nav.topbar.filterByProperty")}
              >
                <option value="all">{t("nav.topbar.allProperties")}</option>
                {properties.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="top-bar-right">
            <LanguageSwitcher className="form-control desktop-only" style={{ height: "28px", fontSize: "12px", padding: "0 6px", width: "auto" }} />
            {/* Notification Bell with Dot */}
            <div style={{ position: "relative" }}>
              <button
                type="button"
                className="notif-btn"
                onClick={() => setIsNotifOpen(!isNotifOpen)}
                aria-label={t("nav.topbar.notifications")}
              >
                <BellIcon size={14} />
                {unreadCount > 0 && <span className="notif-badge-dot" />}
              </button>

              {isNotifOpen && (
                <div className="notif-dropdown">
                  <div
                    style={{
                      padding: "8px 12px",
                      background: "#fafaf9",
                      borderBottom: "1px solid var(--border-subtle)",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <span style={{ fontWeight: 600, fontSize: "12px" }}>{t("nav.topbar.notifications")}</span>
                    {unreadCount > 0 && (
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={handleMarkAllRead}
                        style={{ height: "22px", padding: "0 4px", fontSize: "11px" }}
                      >
                        {t("nav.topbar.markRead")}
                      </button>
                    )}
                  </div>

                  <div style={{ maxHeight: "280px", overflowY: "auto" }}>
                    {notifications.length === 0 ? (
                      <div style={{ padding: "20px", textAlign: "center", color: "var(--text-muted)", fontSize: "12px" }}>
                        {t("nav.topbar.noActivity")}
                      </div>
                    ) : (
                      notifications.slice(0, 6).map((n) => (
                        <div
                          key={n.id}
                          className={`notif-item ${n.is_read ? "" : "unread"}`}
                          onClick={() => handleMarkAsRead(n)}
                        >
                          <div className="notif-item-title">{n.title}</div>
                          <div className="notif-item-message">{n.message}</div>
                          <div className="notif-item-time">{formatDateTime(n.created_at)}</div>
                        </div>
                      ))
                    )}
                  </div>

                  <div
                    style={{
                      padding: "6px 12px",
                      textAlign: "center",
                      borderTop: "1px solid var(--border-subtle)",
                      background: "#fafaf9",
                    }}
                  >
                    <Link
                      href="/notifications"
                      onClick={() => setIsNotifOpen(false)}
                      style={{ fontSize: "11px", color: "var(--text-secondary)", fontWeight: 500 }}
                    >
                      {t("nav.topbar.viewAll")}
                    </Link>
                  </div>
                </div>
              )}
            </div>

            {/* Compact Primary New Reservation Button */}
            {canCreateBookings && (
              <button
                type="button"
                className="btn btn-primary btn-sm desktop-only"
                onClick={() => setIsNewBookingOpen(true)}
              >
                <PlusIcon size={13} />
                <span>{t("nav.topbar.newReservation")}</span>
              </button>
            )}
          </div>
        </header>

        <main className="content-body">
          {impersonation && (
            <div className="error-banner mb-4" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
              <span>{t("billing.admin.impersonation.banner", { name: user?.name ?? "", admin: impersonation.by_email, time: formatDateTime(impersonation.expires_at) })}</span>
              <button type="button" className="btn btn-secondary btn-sm" onClick={exitImpersonation}>{t("billing.admin.impersonation.exit")}</button>
            </div>
          )}
          <BrowserNotifier />
          {billingLocked && !billingAllowedPath ? (
            role === "OWNER" ? null : (
              <div className="empty-state">
                <div className="empty-state-title">{t("billing.owner.lockedStaffTitle")}</div>
                <div className="empty-state-desc">{t("billing.owner.lockedStaff")}</div>
              </div>
            )
          ) : (
            children
          )}
        </main>
      </div>

      {/* Bottom bar on phones, left rail on tablets (see globals.css) */}
      <nav className="mobile-tabbar" aria-label={t("nav.mobile.menu")}>
        {tabItems.map((item, i) => (
          <React.Fragment key={item.href}>
            {i === 2 && canCreateBookings && (
              <button type="button" className="mobile-tab mobile-tab-primary" onClick={() => setIsNewBookingOpen(true)}>
                <span className="mobile-tab-primary-circle">
                  <PlusIcon size={24} />
                </span>
                <span>{t("nav.mobile.book")}</span>
              </button>
            )}
            <Link href={item.href} className={`mobile-tab ${item.active ? "active" : ""}`}>
              <item.Icon size={22} />
              <span>{item.label}</span>
              {item.badge && <span className="mobile-tab-badge">{item.badge}</span>}
            </Link>
          </React.Fragment>
        ))}
        <button type="button" className={`mobile-tab ${menuActive || isMenuOpen ? "active" : ""}`} onClick={() => setIsMenuOpen(true)}>
          <MenuIcon size={22} />
          <span>{t("nav.mobile.menu")}</span>
          {(chatUnread > 0 || unreadCount > 0 || billingLocked) && <span className="mobile-tab-dot" />}
        </button>
      </nav>

      {/* Phone menu sheet: every page in large, grouped tiles, plus organization, language and sign out */}
      {isMenuOpen && (
        <div className="mobile-menu" role="dialog" aria-modal="true" aria-label={t("nav.mobile.menu")}>
          <div className="mobile-menu-header">
            <span className="mobile-menu-title">{t("nav.mobile.menu")}</span>
            <button type="button" className="mobile-menu-close" onClick={() => setIsMenuOpen(false)} aria-label={t("nav.mobile.close")}>
              <XIcon size={22} />
            </button>
          </div>
          <div className="mobile-menu-body">
            {user && (
              <div className="mobile-menu-user">
                <div className="mobile-menu-avatar">{user.name.charAt(0).toUpperCase()}</div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div className="mobile-menu-user-name">{user.name}</div>
                  <div className="mobile-menu-user-role">{role ? tx(`common.roles.${role}`) : t("nav.staff")}</div>
                </div>
              </div>
            )}
            {availableTenants.length > 1 && (
              <label className="mobile-menu-field">
                <span>{t("nav.org.label")}</span>
                <select className="form-control" value={tenantId} onChange={(e) => switchTenant(e.target.value)} aria-label={t("nav.org.ariaLabel")}>
                  {availableTenants.map((tn) => (
                    <option key={tn.tenant_id} value={tn.tenant_id}>
                      {tn.tenant_name} ({tx(`common.roles.${tn.role}`)})
                    </option>
                  ))}
                </select>
              </label>
            )}
            {navGroups.map((group) => (
              <section key={group.label} className="mobile-menu-group">
                <div className="mobile-menu-group-label">{group.label}</div>
                <div className="mobile-menu-grid">
                  {group.items.map((item) => (
                    <Link key={item.href} href={item.href} className={`mobile-menu-tile ${item.active ? "active" : ""}`}>
                      <item.Icon size={22} />
                      <span>{item.label}</span>
                      {item.badge && <span className="nav-badge">{item.badge}</span>}
                    </Link>
                  ))}
                </div>
              </section>
            ))}
            <label className="mobile-menu-field">
              <span>{t("common.language")}</span>
              <LanguageSwitcher />
            </label>
            <button type="button" className="btn btn-secondary mobile-menu-logout" onClick={logout}>
              {t("nav.logout")}
            </button>
          </div>
        </div>
      )}

      {/* Global Booking Modal */}
      {canCreateBookings && (
        <NewBookingModal
          isOpen={isNewBookingOpen}
          onClose={() => setIsNewBookingOpen(false)}
          onSuccess={() => {
            refreshNotifications();
            window.dispatchEvent(new CustomEvent("villa_os_booking_created"));
          }}
        />
      )}
    </div>
  );
}
